import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { Separator } from '@workspace/ui/components/separator';

import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@workspace/ui/components/sidebar';

import type { AuthContextProps } from '~/auth/auth-context';
import { useAuth } from '~/auth/use-auth';
import { AddYourCardComponent } from '~/components/add-your-card';
import { AppSidebar } from '~/components/app-sidebar';
import { DynamicBreadcrumbs } from '~/components/dynamic-breadcrumbs';
import { KycGateProvider } from '~/components/kyc-gate';
import { KycVerificationBanner } from '~/components/kyc-verification-banner';
import { LayoutProvider } from '~/context/layout/layout-context';

export const Route = createRootRouteWithContext<{
  auth: AuthContextProps;
}>()({
  component: RootComponent,
});

function RootComponent() {
  const { isAuthenticated, user, updateMetadata } = useAuth();
  const { pathname } = useLocation();

  if (
    !isAuthenticated ||
    pathname.includes('onboarding') ||
    pathname.includes('organization') ||
    pathname.includes('oauth') ||
    pathname.startsWith('/cli/')
  ) {
    return (
      <main className="flex-1 relative h-full">
        <Outlet />
      </main>
    );
  }

  return (
    <LayoutProvider>
      <KycGateProvider>
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset>
            <header className="flex h-16 shrink-0 items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              <DynamicBreadcrumbs />
            </header>
            <KycVerificationBanner />
            <div className="flex-1 min-h-0">
              <Outlet />
            </div>
          </SidebarInset>

          <Dialog
            open={
              user?.metadata?.kyc_verified &&
              !user?.metadata?.show_add_card_modal
            }
            onOpenChange={() => {
              updateMetadata({ show_add_card_modal: true });
            }}
          >
            <DialogContent className="sm:max-w-[825px]">
              <DialogTitle className="text-lg font-medium mb-4">
                Agrega tu tarjeta a tu billetera
              </DialogTitle>
              <DialogDescription className="mb-4">
                Estás a un paso de poder acceder a finanzas globales con tu
                tarjeta VISA
              </DialogDescription>
              <AddYourCardComponent />
            </DialogContent>
          </Dialog>
        </SidebarProvider>
      </KycGateProvider>
    </LayoutProvider>
  );
}
